import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Modal accessibility helper.
 *
 * - Closes the modal on Escape.
 * - Restores focus to whatever was focused before the modal opened.
 * - Moves focus into the modal (first focusable, or the container itself).
 * - Traps Tab focus so keyboard users cannot leave the modal without closing it.
 *
 * Consumers must pass a ref attached to the modal's outermost card element.
 */
export function useModalA11y({ onClose, containerRef, enabled = true }) {
  const previouslyFocused = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    previouslyFocused.current = typeof document !== 'undefined' ? document.activeElement : null;

    const container = containerRef.current;
    if (container) {
      const firstFocusable = container.querySelector(FOCUSABLE_SELECTOR);
      if (firstFocusable && typeof firstFocusable.focus === 'function') {
        firstFocusable.focus();
      } else {
        if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
        container.focus();
      }
    }

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (onClose) {
          e.stopPropagation();
          onClose();
        }
        return;
      }
      if (e.key !== 'Tab' || !container) return;
      const focusables = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter((el) => !el.hasAttribute('disabled') && !el.hasAttribute('hidden'));
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !container.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const prev = previouslyFocused.current;
      if (prev && typeof prev.focus === 'function' && document.contains(prev)) {
        try { prev.focus(); } catch { /* ignore */ }
      }
    };
  }, [onClose, containerRef, enabled]);
}
